/**
 * Assignment Controls Component
 * Reusable component for assigning jobs/schedules to managers or staff
 * Used by RecurringSchedulesList and JobCard
 */

import { useState } from 'react';
import type { UserProfile } from '../../../../shared/types';
import { SELECT_STYLE, createButtonStyle } from '../../styles/components';
import { ConfirmationDialog } from '../ConfirmationDialog';

interface AssignmentControlsProps {
  // Current assignment state
  assignedManagerId?: string | null;
  userId?: string | null;       // RecurringSchedule (single staff)
  userIds?: string[] | null;    // OneTimeJob (multiple staff)
  assignmentStatus: 'unassigned' | 'assigned_to_manager' | 'assigned_to_staff';

  // Available users
  managers: UserProfile[];
  staff: UserProfile[];

  // Handlers
  onAssignToManager: (managerId: string) => void;
  onAssignToStaff: (staffId: string) => void;
  onUnassign: () => void;

  // State
  isAssigning?: boolean;
  disabled?: boolean;

  // Display options
  showCurrentAssignment?: boolean;
  getUserName: (userId: string) => string;

  // Reassignment confirmation
  confirmReassignment?: boolean;
}

export function AssignmentControls({
  assignedManagerId,
  userId,
  userIds,
  assignmentStatus,
  managers,
  staff,
  onAssignToManager,
  onAssignToStaff,
  onUnassign,
  isAssigning = false,
  disabled = false,
  showCurrentAssignment = true,
  getUserName,
  confirmReassignment = true,
}: AssignmentControlsProps) {
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const isDisabled = isAssigning || disabled;
  const hasManagerAssignment = !!assignedManagerId;
  // Support both userIds (OneTimeJob) and userId (RecurringSchedule)
  const effectiveUserIds = userIds ?? (userId ? [userId] : []);
  const hasStaffAssignment = effectiveUserIds.length > 0;
  const isAssigned = assignmentStatus !== 'unassigned';

  // Get current assignment display text
  const getAssignmentDisplay = () => {
    if (effectiveUserIds.length > 0) {
      return `Staff: ${effectiveUserIds.map(getUserName).join(', ')}`;
    }
    if (assignedManagerId) {
      return `Manager: ${getUserName(assignedManagerId)}`;
    }
    return 'Unassigned';
  };

  // Handle assignment with optional reassignment confirmation
  const handleAssignment = (
    handler: (userId: string) => void,
    newUserId: string,
    userType: 'manager' | 'staff'
  ) => {
    if (!newUserId) return;

    if (confirmReassignment && isAssigned) {
      const currentAssignment = getAssignmentDisplay();
      const action =
        userType === 'manager'
          ? 'reassign to a different manager'
          : assignedManagerId
            ? 'reassign directly to staff (bypassing manager)'
            : 'reassign to different staff';

      setConfirmDialog({
        title: 'Confirm Reassignment',
        message: `Currently assigned to ${currentAssignment}. Do you want to ${action}?`,
        onConfirm: () => { setConfirmDialog(null); handler(newUserId); },
      });
      return;
    }

    handler(newUserId);
  };

  // Handle unassign with confirmation
  const handleUnassign = () => {
    setConfirmDialog({
      title: 'Remove Assignment',
      message: `Remove assignment from ${getAssignmentDisplay()}? This will return the job to unassigned status.`,
      onConfirm: () => { setConfirmDialog(null); onUnassign(); },
    });
  };

  // Get placeholder text based on current state
  const getManagerPlaceholder = () => {
    if (hasStaffAssignment) return 'Select Manager';
    return isAssigned ? 'Reassign to Manager...' : 'Assign to Manager...';
  };

  const getStaffPlaceholder = () => {
    if (hasManagerAssignment) return 'Select Staff';
    return isAssigned ? 'Reassign to Staff...' : 'Assign to Staff (Direct)...';
  };

  return (
    <>
    {confirmDialog && (
      <ConfirmationDialog
        isOpen={true}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Continue"
        confirmStyle="warning"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    )}
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>Assign to:</span>

      {/* Manager Dropdown - disabled if assigned to staff */}
      {managers.length > 0 && (
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) {
              handleAssignment(onAssignToManager, e.target.value, 'manager');
              e.target.value = ''; // Reset dropdown
            }
          }}
          disabled={isDisabled || hasStaffAssignment}
          style={{
            ...SELECT_STYLE,
            cursor: isDisabled || hasStaffAssignment ? 'not-allowed' : 'pointer',
            opacity: hasStaffAssignment ? 0.5 : 1,
          }}
          title={hasStaffAssignment ? 'Unassign staff first to assign to manager' : undefined}
        >
          <option value="">{getManagerPlaceholder()}</option>
          {managers.map((m) => (
            <option key={m.uid} value={m.uid}>
              {m.firstName} {m.lastName}
            </option>
          ))}
        </select>
      )}

      <span style={{ fontSize: '12px', color: '#9ca3af' }}>or</span>

      {/* Staff Dropdown - disabled if assigned to manager */}
      {staff.length > 0 && (
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) {
              handleAssignment(onAssignToStaff, e.target.value, 'staff');
              e.target.value = ''; // Reset dropdown
            }
          }}
          disabled={isDisabled || hasManagerAssignment}
          style={{
            ...SELECT_STYLE,
            cursor: isDisabled || hasManagerAssignment ? 'not-allowed' : 'pointer',
            opacity: hasManagerAssignment ? 0.5 : 1,
          }}
          title={hasManagerAssignment ? 'Unassign manager first to assign directly to staff' : undefined}
        >
          <option value="">{getStaffPlaceholder()}</option>
          {staff.map((s) => (
            <option key={s.uid} value={s.uid}>
              {s.firstName} {s.lastName}
            </option>
          ))}
        </select>
      )}

      {/* Current Assignment & Unassign */}
      {showCurrentAssignment && isAssigned && (
        <>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            Currently: {getAssignmentDisplay()}
          </span>
          <button
            onClick={handleUnassign}
            disabled={isDisabled}
            style={{
              ...createButtonStyle('warning'),
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            Remove Assignment
          </button>
        </>
      )}
    </div>
    </>
  );
}
